import KkmBackendApi from '@/services/kkm-server/KkmBackendApi'
import kkmServerApiClient from '@/services/kkm-server/kkmServerApiClient'
import { kkmJournal } from '@/services/kkm-server/journal'
import {
  STAGES,
  canRetryProbe,
  generateClaimerToken,
  isAbandoned,
  isWithinDeadline,
  nextProbeDelayMs,
} from '@/services/kkm-server/pipeline-core'
import { websocketService } from '@/services/WebsocketService'
import kkmWatchdog from '@/services/kkm-server/kkmWatchdog'
import CommandStatus from '@/models/enums/kkm-server/CommandStatus'

const RECOVERY_INTERVAL_MS = 60000
const GET_REZULT_POLL_MS = 2000

// KKM-Server отдаёт Run=1 / NotRun=4, пока команда выполняется; 5 — старое значение, оставлено
const RUNNING_STATUSES = [CommandStatus.Run, CommandStatus.NotRun, 5]
const isRunning = result => RUNNING_STATUSES.includes(result?.Status)

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Прокси доставки ККМ-команд. Три слоя идемпотентности:
 * Web Locks (одна вкладка-лидер на браузер) -> серверный клейм (одна машина
 * на филиал) -> IndexedDB-журнал (восстановление после сбоев).
 * Инвариант журнала: стадия пишется ДО внешнего действия.
 */
class KkmServerProxy {
  init ({ filialId, token }) {
    if (this._inited) return
    this._inited = true
    this._filialId = filialId
    this._token = token
    this.backendApi = new KkmBackendApi(token)

    if (navigator.locks) {
      navigator.locks.request('kkm-proxy:' + filialId, () => {
        this._becomeLeader()
        return new Promise(() => {}) // держим лок до закрытия вкладки
      })
    } else {
      // фолбэк: все вкладки слушают, от дублей защищает серверный клейм
      this._becomeLeader()
    }
  }

  _becomeLeader () {
    console.info('KkmServerProxy: эта вкладка - лидер, filial', this._filialId)

    const echo = websocketService.getEcho()
    // авторизация kkm-канала ключом филиала: заголовок уедет вместе с JWT,
    // бэк различает каналы по имени (BroadcastAuthController)
    echo.connector.options.auth.headers.Token = this._token
    echo.private('print.kkm_crm_proxy.' + this._filialId)
      .listen('.new_kkm_command', command => this.processCommand(command))

    this._recover()
    setInterval(() => this._recover(), RECOVERY_INTERVAL_MS)
    window.addEventListener('online', () => this._recover())
    kkmJournal.cleanup()

    // вочдог KkmServer - только у лидера, чтобы две вкладки не слали два запуска
    kkmWatchdog.start()
  }

  async processCommand (command) {
    const commandId = String(command.id)
    // ре-бродкаст каждые 5 сек: команду, которая уже в работе, не трогаем -
    // повторный запуск конвейера дал бы второй Execute того же IdCommand
    if (this._isInFlight(commandId)) return

    const record = {
      commandId,
      backendCommandId: command.command_id,
      payload: command.data,
      deadlineAt: command.deadline_at || null,
      stage: STAGES.Received,
      claimerToken: generateClaimerToken(),
      sendAttempts: 0,
      result: null,
    }
    await this._withInFlight(commandId, async () => {
      await kkmJournal.put(record)
      await this._runFromReceived(record)
    })
  }

  _isInFlight (commandId) {
    if (!this._inFlight) this._inFlight = new Set()
    return this._inFlight.has(commandId)
  }

  async _withInFlight (commandId, fn) {
    if (!this._inFlight) this._inFlight = new Set()
    this._inFlight.add(commandId)
    try {
      await fn()
    } finally {
      this._inFlight.delete(commandId)
    }
  }

  async _runFromReceived (record) {
    let claimed = false
    try {
      claimed = await this.backendApi.claim(record.commandId, record.claimerToken)
    } catch (e) {
      // бэк недоступен: запись убираем, ре-бродкаст доставит команду снова
      console.warn('KkmServerProxy: claim не удался', e)
      await kkmJournal.remove(record.commandId)
      return
    }
    if (!claimed) {
      await kkmJournal.remove(record.commandId)
      return
    }
    record.stage = STAGES.Claimed
    await kkmJournal.put(record)
    await this._runFromClaimed(record)
  }

  async _runFromClaimed (record) {
    // проба до Execute: её провал = команда гарантированно не уходила,
    // только такие ошибки можно ретраить
    while (true) {
      try {
        await kkmServerApiClient.probe()
        break
      } catch (e) {
        kkmWatchdog.kick()
        if (!canRetryProbe(record.sendAttempts) || !isWithinDeadline(record.deadlineAt)) {
          try {
            await this.backendApi.release(record.commandId)
          } catch (releaseError) {
            console.warn('KkmServerProxy: release не удался', releaseError)
          }
          await kkmJournal.remove(record.commandId)
          return
        }
        const delayMs = nextProbeDelayMs(record.sendAttempts)
        record.sendAttempts += 1
        await kkmJournal.put(record)
        await sleep(delayMs)
      }
    }

    record.stage = STAGES.Sent
    await kkmJournal.put(record)

    let result
    try {
      result = await this._executeWithPolling(record)
    } catch (e) {
      // исход неизвестен: Execute не повторяем, судьбу решит восстановление
      // (GetRezult) или прицельный разбор на бэке
      console.warn('KkmServerProxy: исход Execute неизвестен', record.commandId, e)
      return
    }

    await this._deliverResult(record, result)
  }

  async _executeWithPolling (record) {
    const executeTimeout = Math.max(
      15000,
      record.deadlineAt ? new Date(record.deadlineAt).getTime() - Date.now() : 15000,
    )
    let result = await kkmServerApiClient.sendCommand(record.payload, executeTimeout)

    // команда выполняется: опрос GetRezult до дедлайна
    while (isRunning(result) && isWithinDeadline(record.deadlineAt)) {
      await sleep(GET_REZULT_POLL_MS)
      const response = await kkmServerApiClient.sendCommand(
        { Command: 'GetRezult', IdCommand: record.backendCommandId },
        5000,
      )
      result = response.Rezult || response
    }

    if (isRunning(result)) {
      throw new Error('Команда всё ещё выполняется, дедлайн истёк')
    }
    return result
  }

  async _deliverResult (record, result) {
    record.stage = STAGES.Done
    record.result = result
    await kkmJournal.put(record)

    // Все финальные результаты (успех и ошибки) идут через status: бэк сам
    // разбирает isSuccess/hasError и сохраняет payload ответа ККМ
    const delivered = await this.backendApi.sendCommandStatusWithRetries(record.commandId, result)

    if (delivered) {
      await kkmJournal.remove(record.commandId)
    }
  }

  async _tryOnce (fn) {
    try {
      await fn()
      return true
    } catch (e) {
      return false
    }
  }

  // восстановление по журналу: у каждой стадии детерминированное действие
  async _recover () {
    if (this._recovering) return
    this._recovering = true
    try {
      const records = await kkmJournal.all()
      for (const record of records) {
        if (this._isInFlight(record.commandId)) continue
        try {
          await this._withInFlight(record.commandId, () => this._recoverRecord(record))
        } catch (e) {
          console.warn('KkmServerProxy: восстановление не удалось', record.commandId, e)
        }
      }
    } finally {
      this._recovering = false
    }
  }

  async _recoverRecord (record) {
    switch (record.stage) {
      case STAGES.Received:
        // клейм идемпотентен по claimerToken - безопасно повторить
        await this._runFromReceived(record)
        break
      case STAGES.Claimed:
        if (isWithinDeadline(record.deadlineAt)) {
          await this._runFromClaimed(record)
        } else {
          await this._tryOnce(() => this.backendApi.release(record.commandId))
          await kkmJournal.remove(record.commandId)
        }
        break
      case STAGES.Sent:
        if (isAbandoned(record.deadlineAt)) {
          await kkmJournal.patch(record.commandId, { stage: STAGES.Abandoned })
          break
        }
        await this._recoverSent(record)
        break
      case STAGES.Done:
        await this._deliverResult(record, record.result)
        break
      case STAGES.Abandoned:
        break // чистится по TTL
    }
  }

  async _recoverSent (record) {
    const response = await kkmServerApiClient.sendCommand(
      { Command: 'GetRezult', IdCommand: record.backendCommandId },
      5000,
    )
    const result = response.Rezult || response
    // 0=Ok, 2=Error, 3=NotFound - финальные исходы; 1/4 - ещё выполняется
    if (result.Status === 0 || result.Status === 2 || result.Status === 3) {
      await this._deliverResult(record, result)
    }
  }
}

// This is a singleton class that will be used to communicate with the KkmServer extension
const kkmServerProxy = new KkmServerProxy()

export default kkmServerProxy
